import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext.jsx';
import useMovieLibrary from '../hooks/useMovieLibrary.js';
import useCategories from '../hooks/useCategories.js';
import useRecommendations from '../hooks/useRecommendations.js';

const LibraryContext = createContext();

export const useLibraryContext = () => {
  const context = useContext(LibraryContext);
  if (!context) {
    throw new Error('useLibraryContext must be used within LibraryProvider');
  }
  return context;
};

export const LibraryProvider = ({ children, showSnackbar }) => {
  const { currentUser } = useAuth();

  // Movie library hook
  const library = useMovieLibrary(currentUser, showSnackbar);

  // Categories hook
  const categories = useCategories(currentUser, showSnackbar);

  // Recommendations hook
  const recommendations = useRecommendations(showSnackbar);

  // Helper function to get filtered library movies
  const getFilteredLibraryMovies = (activeCategoryId) => {
    return library.userLibrary.filter(
      (movie) =>
        activeCategoryId === 'all' || movie.categories.includes(activeCategoryId)
    );
  };

  // Helper function to handle recommendations with active category
  const handleGetRecommendationsForCategory = (activeCategoryId) => {
    const filteredMovies = getFilteredLibraryMovies(activeCategoryId);
    const activeCategoryObj = categories.categories.find((c) => c.id === activeCategoryId);
    recommendations.handleGetRecommendations(filteredMovies, activeCategoryObj);
  };

  const value = {
    // Library
    userLibrary: library.userLibrary,
    addMovieToLibrary: library.addMovieToLibrary,
    removeMovieFromLibrary: library.removeMovieFromLibrary,

    // Categories
    categories: categories.categories,
    activeCategory: categories.activeCategory,
    showAddCategoryModal: categories.showAddCategoryModal,
    showAddToCategoryModal: categories.showAddToCategoryModal,
    newCategoryName: categories.newCategoryName,
    categoryErrorMessage: categories.categoryErrorMessage,
    selectedMovieForCategory: categories.selectedMovieForCategory,
    openAddCategoryModal: categories.openAddCategoryModal,
    closeAddCategoryModal: categories.closeAddCategoryModal,
    openAddToCategoryModal: categories.openAddToCategoryModal,
    closeAddToCategoryModal: categories.closeAddToCategoryModal,
    createCategory: categories.createCategory,
    toggleMovieCategory: categories.toggleMovieCategory,
    handleActiveCategoryChange: categories.handleActiveCategoryChange,
    setActiveCategory: categories.setActiveCategory,
    setNewCategoryName: categories.setNewCategoryName,

    // Recommendations
    showRecommendationsModal: recommendations.showRecommendationsModal,
    recommendationsList: recommendations.recommendations,
    isGeneratingRecommendations: recommendations.isGeneratingRecommendations,
    recommendationError: recommendations.recommendationError,
    handleGetRecommendations: recommendations.handleGetRecommendations,
    closeRecommendationsModal: recommendations.closeRecommendationsModal,

    // Helper functions
    getFilteredLibraryMovies,
    handleGetRecommendationsForCategory,
  };

  return (
    <LibraryContext.Provider value={value}>
      {children}
    </LibraryContext.Provider>
  );
};
